/**
 * Mobile sticky add-to-cart bar.
 * Mirrors the real product form instead of reimplementing it: the button
 * forwards its click to the page's own add-to-cart button, so variant
 * selection, quantity, cart drawer and error handling all stay untouched.
 * The theme morphs the DOM on variant changes, so every node lookup is
 * re-resolved rather than cached.
 *
 * While no size has been chosen (the page clears the default size), the
 * button reads "Choose your size" instead of "Add to cart". A tap then goes
 * through the page's own size guard, which scrolls to the size picker.
 *
 * The bar watches the page for changes, so it must never be the cause of
 * one: it observes the main content only (the bar itself sits outside it),
 * and every write is guarded so an unchanged value never touches the DOM.
 * Without both guards the observer sees its own writes and re-syncs on
 * every animation frame, forever.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.labelAdd = this.dataset.labelAdd || 'Add to cart';
    this.labelSoldOut = this.dataset.labelSoldOut || 'Sold out';
    this.labelChooseSize = this.dataset.labelChooseSize || 'Choose your size';

    this.btn = this.querySelector('[data-satc-btn]');
    this.btnText = this.querySelector('[data-satc-btn-text]');
    this.now = this.querySelector('[data-satc-now]');
    this.was = this.querySelector('[data-satc-was]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-satc-variants]').textContent);
    } catch {
      this.variants = {};
    }

    if (!this.btn || !this.anchor()) return;
    this.hidden = false;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Show only once the real button has scrolled off the top of the viewport.
        this.toggleAttribute(
          'data-shown',
          !entry.isIntersecting && entry.boundingClientRect.bottom <= 0
        );
      },
      { threshold: 0 }
    );
    this.reobserve();

    this.btn.addEventListener('click', this.onClick);

    this.mutations = new MutationObserver(this.queueSync);
    this.mutations.observe(this.watchRoot(), {
      subtree: true,
      childList: true,
      attributes: true,
      // checked / data-current-checked / aria-checked / class: the size picker
      // (native radios and the swatch app) signals a chosen or cleared size
      // through these, not through value.
      attributeFilter: ['value', 'disabled', 'checked', 'data-current-checked', 'aria-checked', 'class'],
    });
    document.addEventListener('change', this.queueSync);
    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutations?.disconnect();
    this.btn?.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.queueSync);
    if (this.pending) cancelAnimationFrame(this.pending);
  }

  /** The subtree worth watching: the product form's, never the bar's own. */
  watchRoot() {
    return (
      this.form()?.closest('.shopify-section') ||
      document.querySelector('.product-information') ||
      document.getElementById('MainContent') ||
      document.body
    );
  }

  /** The page's own product form for this product. */
  form() {
    const sel = `product-form-component[data-product-id="${this.productId}"]`;
    return document.querySelector(`.product-information ${sel}`) || document.querySelector(sel);
  }

  /** The real add-to-cart button. */
  realButton() {
    const form = this.form();
    return form?.querySelector('.add-to-cart-button') || form?.querySelector('button[name="add"]') || null;
  }

  /** The element whose scroll position decides whether the bar shows. */
  anchor() {
    const real = this.realButton();
    return real?.closest('.buy-buttons-block') || real || null;
  }

  /** The size option groups of the product form (legend starting with "Size"). */
  sizeSets() {
    const root = this.form()?.closest('.shopify-section') || document.querySelector('.product-information');
    if (!root) return [];
    return [].filter.call(root.querySelectorAll('fieldset'), (set) => {
      const legend = set.querySelector('legend');
      return !!legend && /^\s*Size\b/i.test(legend.textContent);
    });
  }

  /** True when the product has no size option, or a size has been picked. */
  sizeChosen() {
    const sets = this.sizeSets();
    if (!sets.length) return true;
    return sets.some((set) => {
      if (set.querySelector('input[type="radio"]:checked, input[type="radio"][data-current-checked="true"]')) return true;
      return !!set.querySelector('.swatch-selected, [aria-checked="true"]');
    });
  }

  /** Re-point the IntersectionObserver if the theme replaced the anchor node. */
  reobserve() {
    const anchor = this.anchor();
    if (!anchor || anchor === this.observed) return;
    if (this.observed) this.observer.unobserve(this.observed);
    this.observer.observe(anchor);
    this.observed = anchor;
  }

  onClick = (event) => {
    event.preventDefault();
    const real = this.realButton();
    if (real && !real.disabled) real.click();
  };

  queueSync = (records) => {
    // Never react to our own writes, whatever root we ended up observing.
    if (records?.length && records.every((record) => this.contains(record.target))) return;
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = null;
      this.sync();
    });
  };

  /** Write only on a real change, so a sync that changes nothing is silent. */
  setText(element, value) {
    if (!element || element.textContent === value) return;
    element.textContent = value;
  }

  sync() {
    if (this.observed && !this.observed.isConnected) this.observed = null;
    this.reobserve();

    const id = this.form()?.querySelector('input[name="id"]')?.value;
    const data = id ? this.variants[id] : null;
    const real = this.realButton();
    const sellable = data ? data.a : !real?.disabled;
    const needSize = sellable && !this.sizeChosen();

    if (data) {
      this.setText(this.now, data.p);
      this.setText(this.was, data.c || '');
      this.was?.toggleAttribute('hidden', !data.c);
    }

    this.setText(this.btnText, !sellable ? this.labelSoldOut : needSize ? this.labelChooseSize : this.labelAdd);
    this.btn.toggleAttribute('disabled', !sellable);
    if (this.getAttribute('data-need-size') !== (needSize ? '' : null)) {
      this.toggleAttribute('data-need-size', needSize);
    }
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
